import type {
    AnimatedIconTransitionDefinition,
    AnimatedIconTransitionRow,
    AnimatedIconTime
} from "./type";
import {
    MOTION_ICON_APPEAR_SCALE,
    MOTION_ICON_APPEAR_BLUR
} from "../../helpers/motion";

const MEDIUM: AnimatedIconTime = [
    "medium",
    1
];

const SHORT: AnimatedIconTime = [
    "short",
    1
];

const SHORT_HALF: AnimatedIconTime = [
    "short",
    0.5
];

const SWAP_ROW: AnimatedIconTransitionRow = {
    s: MOTION_ICON_APPEAR_SCALE,
    b: MOTION_ICON_APPEAR_BLUR,
    duration: MEDIUM,
    motion: "go",
    part: "all",
    op: 0
};

export const ANIMATED_ICON_SWAP_TRANSITION: AnimatedIconTransitionDefinition = {
    to: [
        {
            ...SWAP_ROW,
            motion: "come"
        }
    ],
    total: [MEDIUM],
    from: [
        SWAP_ROW
    ]
};

export const ANIMATED_ICON_REDUCED_TRANSITION: AnimatedIconTransitionDefinition = {
    from: [
        {
            duration: SHORT,
            motion: "go",
            part: "all",
            op: 0
        }
    ],
    to: [
        {
            duration: SHORT,
            motion: "come",
            part: "all",
            op: 0
        }
    ],
    total: [SHORT]
};

export const ANIMATED_ICON_TRANSITIONS: Record<string, AnimatedIconTransitionDefinition> = {
    "chevron-right>chevron-down": {
        from: [
            {
                duration: MEDIUM,
                motion: "go",
                part: 0,
                r: 90
            }
        ],
        total: [MEDIUM],
        to: []
    },
    "check>check-check": {
        to: [
            {
                delay: SHORT_HALF,
                duration: MEDIUM,
                isReversed: true,
                motion: "draw",
                easing: "out",
                part: 1
            }
        ],
        from: [
            {
                duration: MEDIUM,
                motion: "go",
                part: 0,
                x: -2
            }
        ],
        total: [
            MEDIUM,
            SHORT_HALF
        ]
    },
    "copy>check": {
        to: [
            {
                delay: SHORT_HALF,
                duration: MEDIUM,
                isReversed: true,
                motion: "draw",
                easing: "out",
                part: 0
            }
        ],
        from: [
            {
                ...SWAP_ROW,
                part: "all"
            }
        ],
        total: [
            MEDIUM,
            SHORT_HALF
        ]
    },
    "eye>eye-closed": {
        to: [
            {
                duration: MEDIUM,
                motion: "come",
                part: 1,
                op: 0,
                y: 2
            },
            {
                delay: SHORT_HALF,
                duration: MEDIUM,
                isReversed: true,
                motion: "draw",
                easing: "out",
                part: [
                    0,
                    2,
                    3,
                    4
                ]
            }
        ],
        from: [
            {
                duration: MEDIUM,
                motion: "go",
                origin: [
                    12,
                    14
                ],
                sy: 0.3,
                part: [
                    0,
                    1
                ],
                op: 0
            }
        ],
        total: [
            MEDIUM,
            SHORT_HALF
        ]
    },
    "volume-2>volume-x": {
        to: [
            {
                delay: SHORT_HALF,
                duration: SHORT,
                motion: "draw",
                easing: "out",
                part: 1
            },
            {
                duration: SHORT,
                motion: "draw",
                easing: "out",
                delay: SHORT,
                part: 2
            }
        ],
        from: [
            {
                duration: SHORT,
                motion: "go",
                origin: [
                    15,
                    12
                ],
                part: [
                    1,
                    2
                ],
                op: 0,
                s: 0.4
            }
        ],
        total: [
            MEDIUM,
            SHORT_HALF
        ]
    },
    "arrow-up-down>arrow-down": {
        from: [
            {
                motion: "go",
                origin: [
                    17,
                    20
                ],
                part: 0,
                s: 1.75,
                x: -5,
                y: -1
            },
            {
                motion: "go",
                sy: 0.875,
                origin: [
                    17,
                    20
                ],
                part: 1,
                x: -5,
                y: -1
            },
            {
                duration: MEDIUM,
                motion: "go",
                origin: [
                    7,
                    12
                ],
                part: [
                    2,
                    3
                ],
                s: 0.6,
                op: 0
            }
        ],
        total: [MEDIUM],
        to: []
    },
    "arrow-up-down>arrow-up": {
        from: [
            {
                motion: "go",
                origin: [
                    7,
                    4
                ],
                part: 2,
                s: 1.75,
                x: 5,
                y: 1
            },
            {
                motion: "go",
                sy: 0.875,
                origin: [
                    7,
                    4
                ],
                part: 3,
                x: 5,
                y: 1
            },
            {
                duration: MEDIUM,
                motion: "go",
                origin: [
                    17,
                    12
                ],
                part: [
                    0,
                    1
                ],
                s: 0.6,
                op: 0
            }
        ],
        total: [MEDIUM],
        to: []
    },
    "menu>x": {
        from: [
            {
                motion: "go",
                origin: [
                    12,
                    5
                ],
                sx: 1.06,
                part: 0,
                r: 45,
                y: 7
            },
            {
                duration: MEDIUM,
                motion: "go",
                part: 1,
                op: 0,
                sx: 0
            },
            {
                motion: "go",
                origin: [
                    12,
                    19
                ],
                sx: 1.06,
                part: 2,
                r: -45,
                y: -7
            }
        ],
        total: [MEDIUM],
        to: []
    },
    "plus>minus": {
        from: [
            {
                duration: MEDIUM,
                motion: "go",
                part: 1,
                sy: 0
            }
        ],
        total: [MEDIUM],
        to: []
    },
    "bookmark>bookmark-check": {
        to: [
            {
                motion: "draw",
                easing: "out",
                part: 1
            }
        ],
        total: [MEDIUM],
        from: []
    },
    "chevron-down>chevron-up": {
        from: [
            {
                motion: "go",
                part: 0,
                r: 180
            }
        ],
        total: [MEDIUM],
        to: []
    },
    "bell>bell-off": {
        to: [
            {
                motion: "come",
                part: [
                    1,
                    3
                ],
                op: 0
            },
            {
                motion: "draw",
                easing: "out",
                part: 2
            }
        ],
        from: [
            {
                motion: "go",
                part: 1,
                op: 0
            }
        ],
        total: [MEDIUM]
    },
    "eye>eye-off": {
        to: [
            {
                motion: "come",
                part: [
                    0,
                    1,
                    2
                ],
                op: 0
            },
            {
                motion: "draw",
                easing: "out",
                part: 3
            }
        ],
        from: [
            {
                motion: "go",
                part: [
                    0,
                    1
                ],
                op: 0
            }
        ],
        total: [MEDIUM]
    },
    "mic>mic-off": {
        to: [
            {
                motion: "come",
                part: [
                    1,
                    2,
                    3,
                    5
                ],
                op: 0
            },
            {
                motion: "draw",
                easing: "out",
                part: 4
            }
        ],
        from: [
            {
                motion: "go",
                part: [
                    1,
                    2
                ],
                op: 0
            }
        ],
        total: [MEDIUM]
    },
    "plus>x": {
        to: [
            {
                motion: "come",
                s: 0.826,
                part: [
                    0,
                    1
                ],
                r: -45,
                op: 0
            }
        ],
        from: [
            {
                motion: "go",
                s: 1.21,
                part: [
                    0,
                    1
                ],
                op: 0,
                r: 45
            }
        ],
        total: [MEDIUM]
    },
    "sun>moon": {
        to: [
            {
                ...SWAP_ROW,
                motion: "come",
                r: -90
            }
        ],
        from: [
            {
                ...SWAP_ROW,
                r: 90
            }
        ],
        total: [MEDIUM]
    },
    "wifi>wifi-off": {
        to: [
            {
                motion: "come",
                part: [
                    2,
                    3,
                    4,
                    5
                ],
                op: 0
            },
            {
                motion: "draw",
                easing: "out",
                part: 6
            }
        ],
        from: [
            {
                motion: "go",
                part: [
                    1,
                    2
                ],
                op: 0
            }
        ],
        total: [MEDIUM]
    },
    "arrow-up>arrow-down": {
        from: [
            {
                motion: "go",
                part: [
                    0,
                    1
                ],
                r: 180
            }
        ],
        total: [MEDIUM],
        to: []
    },
    "lock>lock-open": {
        from: [
            {
                motion: "go",
                f: 0.789,
                part: 1
            }
        ],
        total: [MEDIUM],
        to: []
    }
};
